"use client";

import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "motion/react";
import { cva } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { Image } from "@/components/ui/Image";
import { Video } from "@/components/ui/Video";
import { useMediaQuery } from "@/lib/hooks/useMediaQuery";
import { TextBlockContent } from "./Text";
import type { ButtonConfig, MediaData, ThemeVariant } from "@/types";
import { HeadingStyle, HeadingTag } from "../ui/Heading";

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/**
 * How many viewport-heights the component occupies per slide to slow down scroll.
 */
const SCROLL_SPEED_FACTOR = 250;

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

const lineIndicatorVariants = cva(
  "block w-0.75 transition-all duration-300 cursor-pointer border-0 p-0 rounded-full relative",
  {
    variants: {
      variant: {
        light: "bg-white/30 hover:bg-yellow",
        dark: "bg-black/30 hover:bg-purple",
      },
      active: {
        true: "h-20",
        false: "h-20",
      },
    },
    compoundVariants: [
      { variant: "light", active: true, className: "bg-yellow" },
      { variant: "dark", active: true, className: "bg-purple" },
    ],
    defaultVariants: { variant: "light", active: false },
  }
);

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface VerticalSlide {
  topline?: string;
  headline?: string;
  text?: string;
  headingTag?: HeadingTag;
  headingStyle?: HeadingStyle;
  button?: ButtonConfig;
  media: MediaData;
}

export interface VerticalSliderBlockProps {
  slides: VerticalSlide[];
  /** Controls image position for every slide. */
  mediaPosition?: "left" | "right";
  variant?: ThemeVariant;
  className?: string;
}

// ---------------------------------------------------------------------------
// SlidePanel – one horizontal slide
// ---------------------------------------------------------------------------

interface SlidePanelProps {
  slide: VerticalSlide;
  mediaPosition: "left" | "right";
  variant: "light" | "dark";
}

const SlidePanel: React.FC<SlidePanelProps> = ({
  slide,
  mediaPosition,
  variant,
}) => {
  const isImageLeft = mediaPosition !== "right";

  const renderMedia = () => {
    if (slide.media.type === "image" && slide.media.image) {
      return (
        <Image
          {...slide.media.image}
          rounded="lg"
          className="w-full aspect-square object-cover shadow-2xl"
        />
      );
    }
    if (slide.media.type === "video" && slide.media.video) {
      return (
        <Video
          video={slide.media.video}
          className="h-full w-full object-cover rounded-lg shadow-2xl"
        />
      );
    }
    return null;
  };

  return (
    <div className="w-screen shrink-0 md:h-screen flex flex-col justify-center py-xl lg:py-0">
      <div className="container grid grid-cols-12 items-center ">
        {/* ── Image column ─────────────────────────────────────────────────── */}
        <div
          className={cn(
            "col-span-12 lg:col-span-6 flex items-center justify-center",
            isImageLeft
              ? "order-1 lg:col-start-1"
              : "order-1 lg:order-2 lg:col-start-7"
          )}
        >
          <div className="relative w-full aspect-square md:aspect-video lg:aspect-square xl:aspect-[4/3] max-h-[70vh]">
            {renderMedia()}
          </div>
        </div>

        {/* ── Text column ──────────────────────────────────────────────────── */}
        <div
          className={cn(
            "col-span-12 lg:col-span-5 flex flex-col justify-center",
            isImageLeft
              ? "order-2 lg:col-start-7"
              : "order-2 lg:order-1 lg:col-start-1"
          )}
        >
          <TextBlockContent
            subline={slide.topline}
            headline={slide.headline}
            text={slide.text}
            headingTag={slide.headingTag}
            headingStyle={slide.headingStyle}
            button={slide.button}
            variant={variant}
          />
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// VerticalSliderBlock (Now Horizontal Scroll)
// ---------------------------------------------------------------------------

/**
 * VerticalSliderBlock — Transform scroll position to horizontal translation.
 */
export const VerticalSliderBlock: React.FC<VerticalSliderBlockProps> = ({
  slides,
  mediaPosition = "right",
  variant = "light",
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");

  const { scrollYProgress } = useScroll({
    target: isDesktop ? containerRef : undefined,
    offset: ["start start", "end end"],
  });

  // Calculate the horizontal translation
  // We move from 0% (first slide) to a position where the last slide is centered
  // Based on (slides.length - 1) * 100% minus half a screen width (50%)
  const x = useTransform(
    scrollYProgress,
    [0, 1],
    ["0%", `-${100 - 100 / slides.length}%`]
  );

  if (!slides.length) return null;

  if (!isDesktop) {
    return (
      <div className={cn("", className)}>
        {slides.map((slide, i) => (
          <SlidePanel
            key={i}
            slide={slide}
            mediaPosition={mediaPosition}
            variant={variant}
          />
        ))}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={cn("relative", className)}
      style={{
        height: `${(slides.length - 1) * SCROLL_SPEED_FACTOR + 100}vh`,
      }}
    >
      <div className="sticky top-0 h-screen overflow-hidden">
        <motion.div style={{ x }} className="flex h-full w-max">
          {slides.map((slide, i) => (
            <SlidePanel
              key={i}
              slide={slide}
              mediaPosition={mediaPosition}
              variant={variant}
            />
          ))}
        </motion.div>
      </div>
    </div>
  );
};
